/* Instagram feed: optional live fetch via the Instagram API, then a seamless auto-sliding strip. */
class InstagramFeed extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-ig-viewport]');
    this.track = this.querySelector('[data-ig-track]');
    this.notice = this.querySelector('[data-ig-notice]');
    if (!this.track) return;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.speed = parseFloat(this.dataset.speed || '40');
    this.load().then(() => this.build());
    this.onResize = () => this.build();
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    if (this.io) this.io.disconnect();
  }

  async load() {
    const token = (this.dataset.token || '').trim();
    if (!token) return;
    const limit = parseInt(this.dataset.limit || '20', 10);
    const key = this.dataset.cacheKey || 'custom-ig';
    let posts = null;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < 60 * 60 * 1000) posts = cached.posts;
    } catch (e) { /* storage unavailable */ }

    if (!posts) {
      try {
        const url = `https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url,permalink&limit=${limit}&access_token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error?.message || `HTTP ${res.status}`);
        posts = (json.data || []).map((p) => ({
          id: p.id,
          src: p.media_type === 'VIDEO' ? p.thumbnail_url : p.media_url,
          link: p.permalink,
          video: p.media_type === 'VIDEO',
          alt: (p.caption || '').split('\n')[0].slice(0, 140),
        })).filter((p) => p.src);
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), posts })); } catch (e) { /* ignore */ }
      } catch (err) {
        console.warn('[instagram-feed]', err.message);
        this.say(`Instagram feed could not load (${err.message}). Showing the Image blocks instead.`);
        return;
      }
    }

    if (!posts.length) return;
    this.track.innerHTML = posts.map((p) => `
      <li class="custom-ig__tile${p.video ? ' custom-ig__tile--video' : ''}">
        <a class="custom-ig__tile-link" href="${p.link}" target="_blank" rel="noopener">
          <img src="${p.src}" alt="${p.alt.replace(/"/g, '&quot;')}" loading="lazy">
        </a>
      </li>`).join('');
  }

  say(msg) {
    if (!this.notice) return;
    this.notice.textContent = msg;
    this.notice.hidden = false;
  }

  build() {
    // Restore the original tiles, repeat them until one "set" covers the viewport, then append a second
    // identical set and slide by exactly one set width for a seamless loop.
    if (!this.original) this.original = [...this.track.children];
    const real = this.original.filter((li) => !li.classList.contains('custom-ig__tile--placeholder'));
    const tiles = real.length ? real : this.original;
    this.track.innerHTML = '';
    tiles.forEach((li) => this.track.appendChild(li));
    if (!tiles.length) return;

    if (this.reduced) {
      this.classList.add('custom-ig--static');
      return;
    }

    const viewportWidth = this.viewport.clientWidth;
    let copies = 1;
    while (this.track.scrollWidth < viewportWidth * 1.2 && copies < 12) {
      tiles.forEach((li) => this.track.appendChild(li.cloneNode(true)));
      copies += 1;
    }
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const set = [...this.track.children];
    const last = set[set.length - 1];
    const distance = last.offsetLeft + last.offsetWidth - set[0].offsetLeft + gap;
    set.forEach((li) => this.track.appendChild(li.cloneNode(true)));
    // Everything beyond the original tiles is decorative for assistive tech.
    [...this.track.children].slice(tiles.length).forEach((li) => li.setAttribute('aria-hidden', 'true'));

    this.style.setProperty('--ig-half', `${distance}px`);
    this.style.setProperty('--ig-duration', `${Math.max(8, distance / this.speed)}s`);
    this.classList.add('custom-ig--running');
    this.setupDrag();

    if (!this.io) {
      this.io = new IntersectionObserver((entries) => {
        this.classList.toggle('custom-ig--paused', !entries[0].isIntersecting);
      }, { threshold: 0.05 });
      this.io.observe(this);
    }
  }

  setupDrag() {
    if (this.dragReady) return;
    this.dragReady = true;
    let startX = 0;
    let offset = 0;
    let dragging = false;
    const track = this.track;
    const current = () => {
      const m = new DOMMatrixReadOnly(getComputedStyle(track).transform);
      return m.m41;
    };
    this.viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      dragging = true;
      startX = e.clientX;
      offset = current();
      this.classList.add('is-dragging');
      track.style.animation = 'none';
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    });
    const end = () => {
      if (!dragging) return;
      dragging = false;
      this.classList.remove('is-dragging');
      // Resume the animation from the dragged position by restarting with a negative delay.
      const x = current();
      const distance = parseFloat(this.style.getPropertyValue('--ig-half')) || 1;
      const duration = parseFloat(this.style.getPropertyValue('--ig-duration')) || 60;
      const progress = ((-x % distance) + distance) % distance / distance;
      track.style.transform = '';
      track.style.animation = '';
      track.style.animationDelay = `${-progress * duration}s`;
    };
    this.viewport.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      track.style.transform = `translate3d(${offset + (e.clientX - startX)}px, 0, 0)`;
    });
    this.viewport.addEventListener('pointerup', end);
    this.viewport.addEventListener('pointercancel', end);
  }
}

if (!customElements.get('instagram-feed')) customElements.define('instagram-feed', InstagramFeed);
